// src/components/audit/AddFindingModal.jsx
import React, { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import axios from "axios";
import "./AddFindingModal.css";

export default function AddFindingModal({
  show,
  onHide,
  country,
  year,
  onAdded,
  onToast
}) {
  const [submitting, setSubmitting] = useState(false);
  const [newFinding, setNewFinding] = useState({
    finding_id: "",
    previously_identified: "",
    severity: "",
    theme: "",
    finding: "",
    planned_remediation: "",
    owners: "",
    status: "",
    deadline: "",
    is_subsidiary: false,       // ✅ new
    subsidiary_name: ""         // ✅ new
  });

  const handleChange = (field, value) => {
    setNewFinding((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () =>
    setNewFinding({
      finding_id: "",
      previously_identified: "",
      severity: "",
      theme: "",
      finding: "",
      planned_remediation: "",
      owners: "",
      status: "",
      deadline: "",
      is_subsidiary: false,
      subsidiary_name: ""
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newFinding.finding_id) {
      onToast?.("Finding ID is required", "warning");
      return;
    }
    if (!newFinding.severity || !newFinding.finding) {
      onToast?.("Severity and Finding are required", "warning");
      return;
    }

    try {
      setSubmitting(true);

      const payload = { ...newFinding, country, year };
      const res = await axios.post("/api/audit.php", payload, { withCredentials: true });

      if (res.data?.success) {
        onAdded?.(res.data.data);
        onToast?.("Finding added successfully", "success");
        resetForm();
        onHide?.();
      } else {
        onToast?.(res.data?.error || "Error adding finding", "danger");
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err.message || "Network error adding finding";
      onToast?.(msg, "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      size="lg"
      backdrop="static"
      className="add-finding-modal"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Add New Finding</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row g-3">

            {/* Finding ID */}
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Finding ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. FRANCE2025-011"
                  value={newFinding.finding_id}
                  onChange={(e) => handleChange("finding_id", e.target.value.trim())}
                  disabled={submitting}
                />
                <div className="form-text text-muted">
                  Unique ID used in the table header.
                </div>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Previously Identified</Form.Label>
                <Form.Select
                  value={newFinding.previously_identified}
                  onChange={(e) => handleChange("previously_identified", e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Severity</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 7.5"
                  value={newFinding.severity}
                  onChange={(e) => handleChange("severity", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Theme</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Theme"
                  value={newFinding.theme}
                  onChange={(e) => handleChange("theme", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Owner(s)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Owner(s)"
                  value={newFinding.owners}
                  onChange={(e) => handleChange("owners", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            {/* ✅ Subsidiary fields */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Subsidiary?</Form.Label>
                <Form.Select
                  value={newFinding.is_subsidiary ? "Yes" : "No"}
                  onChange={(e) =>
                    handleChange("is_subsidiary", e.target.value === "Yes")
                  }
                  disabled={submitting}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </Form.Select>
              </Form.Group>
            </div>

            {newFinding.is_subsidiary && (
              <div className="col-md-9">
                <Form.Group>
                  <Form.Label>Subsidiary Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter subsidiary name"
                    value={newFinding.subsidiary_name}
                    onChange={(e) =>
                      handleChange("subsidiary_name", e.target.value)
                    }
                    disabled={submitting}
                  />
                </Form.Group>
              </div>
            )}
            {/* ✅ End Subsidiary fields */}

            <div className="col-12">
              <Form.Group>
                <Form.Label>Finding</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Finding description..."
                  value={newFinding.finding}
                  onChange={(e) => handleChange("finding", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label>Planned Remediation</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Remediation plan..."
                  value={newFinding.planned_remediation}
                  onChange={(e) => handleChange("planned_remediation", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={newFinding.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Deadline</Form.Label>
                <Form.Control
                  type="date"
                  value={newFinding.deadline}
                  onChange={(e) => handleChange("deadline", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn-profile btn btn-secondary"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn-profile btn btn-success"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving…
              </>
            ) : (
              "Save Finding"
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
